<template>
  <div class="select-root">
    <base-input
      class="select-input w-100"
      :class="{ active: open }"
      type="text"
      :name="name"
      :id="id"
      :placeholder="placeholder"
      :autosize="false"
      :maxlength="0"
      :readonly="true"
      :value="selectedOption ? selectedOption.label : null"
      @click="handleSelectFocus(!open)"
      @blur="handleSelectFocus(false)"
    />
    <div class="options-list" :class="{ active: open }">
      <ul class="options-list__content">
        <li
          v-for="option in options"
          :key="option.id"
          :class="{ selected: option.selected, no_action: options.length <= 1, option_action: option.action }"
          @click="options.length > 1 ? $emit('change', option.id) : null"
        >
          {{ option.label }}
          <template v-if="option.action">
            <i
              class="material-icons"
              @click.stop="option.action.action(option.id)"
              :title="$t(option.action.icon)"
            >
              {{ option.action.icon }}
            </i>
          </template>
        </li>
        <template v-if="actions && !!actions.length">
          <li class="action" v-for="action in actions" :key="action.label">
            <mini-button-full @click.native="action.action">
              {{ action.label }}
            </mini-button-full>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script async>
const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const MiniButtonFull = () => import(/* webpackChunkName: "vsf-mini-button-full" */ 'theme/components/theme/MiniButtonFull.vue');

export default {
  name: 'CustomSelect',
  components: {
    MiniButtonFull,
    BaseInput
  },
  props: {
    name: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false,
      default: 'custom-select'
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    actions: {
      type: Array,
      required: false,
      default: new Array(0)
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      open: false,
      timeout: null,
      activeOption: null
    }
  },
  computed: {
    selectedOption () {
      return this.options.find(option => option.selected)
    }
  },
  methods: {
    handleSelectFocus (state) {
      if (this.disabled) return;
      this.timeout = setTimeout(() => {
        this.open = state;
      }, 200)
    }
  },
  beforeDestroy () {
    if (this.timeout) clearTimeout(this.timeout);
  }
}
</script>

<style lang="scss" scoped>
.select {
  &-input {
    min-height: unset;
  }
}
</style>

<style lang="scss">
@import '~theme/css/layout/screen';

.select {
  &-input {
    .base-input {
      z-index: 2;
      min-height: fit-content;
      width: -webkit-fill-available;

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 1.15rem;
        right: 10px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 8px 6px 0 6px;
        border-color: #bdbdbd transparent transparent transparent;
        pointer-events: none;
        transition: all 0.3s ease;
      }

      &.active {
        &::after {
          transform: rotate(180deg);
        }
      }

      input {
        height: 40px;
        border-radius: 10px;
        cursor: pointer;
        padding-right: 24px;
        text-overflow: ellipsis;

        @media only screen and (max-width: $screen-md) {
          padding-right: 20px;
        }
      }
    }
  }
}

.options {
  &-list {
    box-sizing: border-box;
    border: 1px solid #bdbdbd;
    border-radius: 8px;
    margin: 0;
    padding: 12px;
    position: absolute;
    background: white;
    z-index: 1;
    top: 36px;
    border-top: none;
    box-shadow: 0px 2px 5px 0px #00000033;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10%);
    transition: all 0.3s ease;
    width: -webkit-fill-available;
    min-width: 200px;
    overflow: hidden;

    &.active {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    &__content {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      text-align: left;
      margin: 0;
      padding: 0;
      row-gap: 6px;
      list-style: none;

      >li {
        padding: 12px 16px;
        transition: all 0.3s ease;
        border-radius: 6px;

        cursor: pointer;

        &:not(.selected):hover {
          background-color: #F2F2F2;
        }

        &.action {
          padding: 0;
          margin-top: 8px;
          width: 100% !important;

          button {
            @media only screen and (max-width: $screen-sm-l) {
              padding: 12px 16px;
              font-weight: 600;
              font-size: 18px;
            }
          }
        }

        &.no_action {
          cursor: default;
        }

        &.option_action {
          position: relative;
          padding-right: 36px;

          i {
            position: absolute;
            top: 10px;
            right: 6px;
            cursor: pointer;
            z-index: 10;
          }
        }

        &.selected {
          color: #111111;
          font-weight: 600;
          border: thin solid #11BB2E;
          position: relative;

          &:not(.no_action):hover {
            filter: brightness(0.95);
          }

          &:after {
            content: '';
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            background-color: #11BB2E;
            opacity: 0.05;
          }
        }

        .button-full {
          min-width: 160px;
        }

        @media only screen and (max-width: $screen-md) {
          .button-full {
            font-size: 16px;
          }
        }
      }
    }

    @media only screen and (max-width: $screen-md) {
      margin: 0;
    }
  }
}
</style>
